import { useState } from 'react';
import { Link } from 'react-router';
import { NeedsAccount } from './AccountInfo';
import { Bell, Trash2 } from 'lucide-react';
import { useI18n } from '../i18n';
import { useApp } from '../state';
import { deleteReminder, PlanLimitError, saveReminder } from '../domain/actions';
import type { HabitRec } from '../domain/model';
import { useSyncState } from '../sync/store';
import { useToast } from '../ui/Toast';
import { Switch } from '../ui/Field';

export function RemindersPanel({ habit }: { habit: HabitRec }) {
  const { t, m } = useI18n();
  const { data } = useApp();
  const sync = useSyncState();
  const toast = useToast();
  const [time, setTime] = useState('20:00');
  const reminders = data.reminders.filter((r) => r.habitId === habit.id);

  const add = async () => {
    try {
      await saveReminder(null, { habitId: habit.id, time, days: null, enabled: true }, reminders.length);
    } catch (err) {
      if (err instanceof PlanLimitError) toast.show(t('notifications.planLimit', { limit: err.limit }));
      else toast.show(t('errors.save'));
    }
  };

  return (
    <section className="card stack" aria-label={t('detail.reminders')}>
      <div className="row">
        <Bell size={20} aria-hidden="true" />
        <h2 className="spacer">{t('detail.reminders')}</h2>
      </div>
      {!sync.signedIn && (
        <NeedsAccount feature="push" />
      )}
      {reminders.length === 0 && <p className="muted small">{t('notifications.none')}</p>}
      {reminders.map((r) => (
        <div key={r.id} className="stack" style={{ gap: 4 }}>
          <div className="row">
            <input
              type="time"
              className="input num"
              style={{ maxWidth: 140 }}
              aria-label={t('notifications.time')}
              value={r.time}
              onChange={(e) => e.target.value && void saveReminder(r.id, { habitId: habit.id, time: e.target.value, days: r.days, enabled: r.enabled }, 0)}
            />
            <span className="spacer muted small">{r.days?.length ? r.days.map((d) => m.weekdays.short[d - 1]).join(' ') : t('notifications.daysScheduled')}</span>
            <button type="button" className="icon-btn" aria-label={t('common.delete')} onClick={() => void deleteReminder(r.id)}>
              <Trash2 size={18} aria-hidden="true" />
            </button>
          </div>
          <Switch
            label={r.time}
            checked={r.enabled}
            onChange={(enabled) => void saveReminder(r.id, { habitId: habit.id, time: r.time, days: r.days, enabled }, 0)}
          />
        </div>
      ))}
      <div className="row">
        <input type="time" className="input num" style={{ maxWidth: 140 }} aria-label={t('notifications.time')} value={time} onChange={(e) => setTime(e.target.value)} />
        <button type="button" className="btn" onClick={add}>
          {t('notifications.add')}
        </button>
      </div>
      {sync.signedIn && (
        <Link to="/settings/notifications" className="small">
          {t('settings.notifications')}
        </Link>
      )}
    </section>
  );
}
